import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FlipButton from 'commons/src/components/buttons/FlipButton';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import DeleteConfirmModal from 'commons/src/components/modals/DeleteConfirmModal';
import { sensorUnits as sensorUnitLabels } from 'commons/src/constants';
import { LocationType } from 'commons/src/models/commonTypeScript';
import CollapsibleSection from '../../../components/CollapsibleSection/CollapsibleSection';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import styles from './mqttPublisher.module.scss';
import { MQTT_EVENT_TYPES, MqttPublisherConfig } from './mqttPublisherTypes';

type Props = {
    config: MqttPublisherConfig;
    onEdit: () => void;
    onToggleActive: (clientId: string, active: boolean) => void;
    deleteConfig: (clientId: string) => Promise<void>;
    deleteLoading: boolean;
    testConnection: (clientId: string) => Promise<void>;
    testConnectionLoading: boolean;
};

const MqttPublisherDetailView = ({
    config,
    onEdit,
    onToggleActive,
    deleteConfig,
    deleteLoading,
    testConnection,
    testConnectionLoading,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const locations: LocationType[] = useSelector((state: Store) => state.locations.locations);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const locationNames = config.subscribedToAll
        ? locations.map(loc => loc.name)
        : locations.filter(loc => config.locations.includes(loc.id)).map(loc => loc.name);
    const maxVisible = 3;
    const buildingsDisplayText =
        locationNames.length <= maxVisible
            ? locationNames.join(', ')
            : `${locationNames.slice(0, maxVisible).join(', ')} ${txt('MqttPublisher.AndMore', {
                  count: locationNames.length - maxVisible,
              })}`;

    const handleDelete = async (): Promise<void> => {
        try {
            await deleteConfig(config.clientId);
            navigate(`/${paths.mqttPublisher}`);
        } catch {
            // toast shown by hook
        }
    };

    const handleTestConnection = async (): Promise<void> => {
        try {
            await testConnection(config.clientId);
        } catch {
            // toast shown by hook
        }
    };

    const isCertificateBased = config.authType === 'CERTIFICATE_BASED';

    const eventTypeLabels = config.eventTypes
        .map(val => MQTT_EVENT_TYPES.find(e => e.value === val))
        .filter((e): e is typeof MQTT_EVENT_TYPES[number] => !!e)
        .map(e => txt(e.label));

    const formatUnit = (key: string, value: string): string => {
        if (key === 'tempUnit') return value === 'c' ? '°C' : '°F';
        return sensorUnitLabels[value] || value;
    };

    const unitsText = Object.entries(config.sensorUnits)
        .map(([key, value]) => formatUnit(key, value))
        .join(' · ');

    return (
        <div>
            <div className="page-wrapper__medium">
                <div className={styles.detailHeader}>
                    <h2 className={styles.detailHeader__name}>{config.name}</h2>
                    <div className={styles.detailHeader__toggle}>
                        <FlipButton
                            id="enable"
                            onClick={(): void => onToggleActive(config.clientId, !config.active)}
                            leftSelected={config.active}
                            leftText="On"
                            rightText="Off"
                        />
                        {!config.active && (
                            <span className={styles.detailHeader__status}>
                                {txt('MqttPublisher.IntegrationDisabled')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection
                    title={txt('MqttPublisher.Connection')}
                    collapsible={false}
                    testAttr="section-connection"
                >
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.ClientId')}</div>
                        <div className={styles.readOnlyField__value}>{config.clientId}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.HostIp')}</div>
                        <div className={styles.readOnlyField__value}>{config.host}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Port')}</div>
                        <div className={styles.readOnlyField__value}>{config.port}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Protocol')}</div>
                        <div className={styles.readOnlyField__value}>{config.protocol}</div>
                    </div>
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection
                    title={txt('MqttPublisher.Authentication')}
                    collapsible={false}
                    testAttr="section-authentication"
                >
                    {isCertificateBased ? (
                        <>
                            <div className={styles.readOnlyField}>
                                <div className={styles.readOnlyField__label}>
                                    {txt('MqttPublisher.ClientCertificate')}
                                </div>
                                <div className={styles.readOnlyField__value}>
                                    <MaterialIcon name="description" /> {txt('MqttPublisher.CertificateUploaded')}
                                </div>
                            </div>
                            <div className={styles.readOnlyField}>
                                <div className={styles.readOnlyField__label}>{txt('MqttPublisher.ClientKey')}</div>
                                <div className={styles.readOnlyField__value}>
                                    <MaterialIcon name="description" /> {txt('MqttPublisher.CertificateUploaded')}
                                </div>
                            </div>
                            <div className={styles.readOnlyField}>
                                <div className={styles.readOnlyField__label}>{txt('MqttPublisher.CaCertificate')}</div>
                                <div className={styles.readOnlyField__value}>
                                    <MaterialIcon name="description" /> {txt('MqttPublisher.CertificateUploaded')}
                                </div>
                            </div>
                        </>
                    ) : (
                        config.username && (
                            <>
                                <div className={styles.readOnlyField}>
                                    <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Username')}</div>
                                    <div className={styles.readOnlyField__value}>{config.username}</div>
                                </div>
                                <div className={styles.readOnlyField}>
                                    <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Password')}</div>
                                    <div className={styles.readOnlyField__value}>••••••••••</div>
                                </div>
                            </>
                        )
                    )}
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection
                    title={txt('MqttPublisher.DataConfigurations')}
                    collapsible={false}
                    testAttr="section-data-config"
                >
                    {config.topicTemplate && (
                        <div className={styles.readOnlyField}>
                            <div className={styles.readOnlyField__label}>{txt('MqttPublisher.TopicFormat')}</div>
                            <div className={styles.readOnlyField__value}>{config.topicTemplate}</div>
                        </div>
                    )}
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.EventTypes')}</div>
                        <div className={styles.readOnlyField__value}>{eventTypeLabels.join(', ')}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Units')}</div>
                        <div className={styles.readOnlyField__value}>{unitsText}</div>
                    </div>
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection
                    title={txt('MqttPublisher.Buildings')}
                    collapsible={false}
                    testAttr="section-buildings"
                >
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Buildings')}</div>
                        <div className={styles.readOnlyField__value}>{buildingsDisplayText}</div>
                    </div>
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <div className={styles.detailButtonRow}>
                    <PrimaryButton
                        onClick={handleTestConnection}
                        type="button"
                        title="MqttPublisher.TestConnection"
                        color="secondary"
                        loading={testConnectionLoading}
                        testAttr="test-mqtt-connection"
                    />
                    <PrimaryButton
                        onClick={(): void => setShowDeleteModal(true)}
                        type="button"
                        title="MqttPublisher.DeleteIntegration"
                        color="alert"
                        loading={deleteLoading}
                        testAttr="delete-mqtt-publisher"
                    />
                    <PrimaryButton
                        onClick={onEdit}
                        type="button"
                        title="MqttPublisher.EditIntegration"
                        color="primary"
                        testAttr="edit-mqtt-publisher"
                    />
                </div>
            </div>

            {showDeleteModal && (
                <DeleteConfirmModal
                    title="MqttPublisher.DeletePublisher"
                    description={txt('MqttPublisher.DeleteDescription', { name: config.name })}
                    onSubmit={handleDelete}
                    onCancel={(): void => setShowDeleteModal(false)}
                    onSubmitText="Delete"
                    onCancelText="Cancel"
                    loading={deleteLoading}
                />
            )}
        </div>
    );
};

export default MqttPublisherDetailView;
